import { useRef, useState } from "react";
import { Box, Typography, LinearProgress, alpha, useTheme } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { uploadLog } from "./api/newTelemetry";
import { ACCENT } from "./accent";

// The backend detects format from the bytes, not the extension; this list is
// the UI's own guard against obviously wrong files.
const ACCEPT = [".bin", ".log", ".ulg", ".ulog", ".tlog"];

const hasValidExtension = (name) => ACCEPT.some((ext) => name.toLowerCase().endsWith(ext));

/**
 * Drag-and-drop / click-to-pick upload for a single flight log — ArduPilot
 * DataFlash, PX4 ULog, or a MAVLink telemetry recording.
 * Drives the full presign→PUT→parse flow, then hands the new log id to the
 * parent to begin polling. This is the app's first real upload surface.
 *
 * `upload` swaps the transport — public pages pass the anonymous flow. Both
 * take (file, { onProgress }) and resolve to a log id.
 */
const LogDropzone = ({ onUploaded, upload = uploadLog }) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null); // null = idle, 0–100 = uploading
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!hasValidExtension(file.name)) {
      setError("Please choose an ArduPilot .bin/.log, PX4 .ulg or MAVLink .tlog file");
      return;
    }
    setError(null);
    // A tlog is the radio link, not the SD card: attitude lands at 4–10 Hz, so
    // the tabs that need kHz-rate data will come back empty. Say so at pick
    // time rather than on the results page.
    setNote(
      file.name.toLowerCase().endsWith(".tlog")
        ? "Telemetry log — GPS, battery, vibration, EKF and motors analyse normally, but the filter, oscillation and PID tabs need a .bin or .ulg."
        : null,
    );
    setProgress(0);
    try {
      const logId = await upload(file, { onProgress: setProgress });
      setProgress(null);
      onUploaded?.(logId);
    } catch (e) {
      setError(e.message);
      setProgress(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (progress !== null) return; // ignore drops mid-upload
    handleFile(e.dataTransfer.files?.[0]);
  };

  const activate = () => progress === null && inputRef.current?.click();

  const uploading = progress !== null;
  const borderColor = dragging ? ACCENT : theme.palette.divider;

  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        onClick={activate}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && activate()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          px: 3,
          py: 6,
          cursor: uploading ? "default" : "pointer",
          borderRadius: 2,
          border: `2px dashed ${borderColor}`,
          backgroundColor: alpha(ACCENT, dragging ? 0.08 : theme.palette.mode === "dark" ? 0.04 : 0.02),
          transition: "border-color 120ms, background-color 120ms",
          outline: "none",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: alpha(ACCENT, 0.12),
          }}
        >
          <UploadFileOutlinedIcon sx={{ color: ACCENT, fontSize: 28 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {uploading ? "Uploading…" : "Drop a flight log here"}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {uploading ? `${progress}%` : "or click to browse — .bin, .log, .ulg or .tlog, up to 200 MB"}
        </Typography>

        <input
          ref={inputRef}
          type="file"
          accept=".bin,.log,.ulg,.ulog,.tlog"
          style={{ display: "none" }}
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = ""; // allow re-selecting the same file
          }}
        />
      </Box>

      {uploading && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 1.5,
            borderRadius: 1,
            height: 6,
            backgroundColor: alpha(ACCENT, 0.2),
            "& .MuiLinearProgress-bar": { backgroundColor: ACCENT },
          }}
        />
      )}
      {note && (
        <Typography variant="body2" sx={{ mt: 1.5, color: theme.palette.text.secondary }}>
          {note}
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default LogDropzone;
